import React, { createContext, useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from './AuthContext';
import VirtualClassRoom from '../components/VirtualClassRoom';
import { Clock, Maximize2, Minimize2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const LiveClassContext = createContext(null);

export const useLiveClass = () => {
    const context = useContext(LiveClassContext);
    if (!context) {
        throw new Error('useLiveClass must be used within a LiveClassProvider');
    }
    return context;
};

export const LiveClassProvider = ({ children }) => {
    const { user } = useAuth();
    const isLecturer = user?.role === 'lecturer';
    const navigate = useNavigate();
    const location = useLocation();

    const [activeSession, setActiveSession] = useState(null); // { roomName, sessionId, isHost, startTime, duration, unitId }
    const [token, setToken] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Track screen size for responsiveness
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-expand when navigating to the Virtual Classroom page
    useEffect(() => {
        if (activeSession && token && location.pathname === '/virtual-classroom') {
            setIsFullScreen(true);
        }
    }, [location.pathname, activeSession, token]);
    const [timeLeft, setTimeLeft] = useState(null);

    // Request media permissions before joining
    const requestMediaPermissions = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true
            });
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (err) {
            console.error('Media permission denied or error:', err);
            alert('Camera and Microphone access are required to join the virtual class. Please check your browser settings.');
            return false;
        }
    };

    const handleJoinSession = async (unitId, sessionId = null) => {
        const permissionsGranted = await requestMediaPermissions();
        if (!permissionsGranted) return { success: false, error: 'Permissions denied' };

        try {
            const endpoint = isLecturer ? '/livekit/create-room' : '/livekit/join-room';
            const body = { unitId };
            if (sessionId) body.sessionId = sessionId;

            if (isLecturer && !sessionId) {
                const durationInput = prompt("Enter class duration in minutes (Max 90):", "90");
                if (durationInput === null) return { success: false, error: 'Cancelled' };
                body.duration = parseInt(durationInput);
            }

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();
            if (response.ok) {
                setToken(data.token);
                setActiveSession({
                    roomName: data.roomName,
                    sessionId: data.sessionId,
                    isHost: isLecturer,
                    startTime: data.startTime || new Date(),
                    duration: data.duration || 90,
                    unitId: unitId
                });
                setIsFullScreen(true);
                return { success: true };
            } else {
                return { success: false, error: data.error || 'Failed to join class' };
            }
        } catch (err) {
            console.error('Join session error:', err);
            return { success: false, error: 'Error joining class' };
        }
    };

    const handleEndClass = async () => {
        if (!activeSession?.sessionId) return;
        if (!window.confirm('Are you sure you want to end this class for everyone?')) return;

        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/livekit/end-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ sessionId: activeSession.sessionId })
            });
            handleDisconnect(null);
        } catch (err) {
            console.error('Error ending session:', err);
            alert('Failed to end session');
        }
    };

    const handleDisconnect = (reason) => {
        if (reason) {
            console.log('Disconnected reason:', reason);
            if (reason !== 'CLIENT_INITIATED_CLOSE' && reason !== 'signal connection lost') {
                alert('The class has ended or you were disconnected.');
            }
        }
        setToken(null);
        setActiveSession(null);
        setIsFullScreen(false);
    };

    // Timer Calculation
    useEffect(() => {
        if (!activeSession || !activeSession.startTime || !activeSession.duration) {
            setTimeLeft(null);
            return;
        }

        const getRemainingTime = () => {
            const start = new Date(activeSession.startTime).getTime();
            const durationMs = activeSession.duration * 60 * 1000;
            const end = start + durationMs;
            const now = new Date().getTime();
            const remaining = end - now;

            if (remaining <= 0) return '00:00:00';

            const hours = Math.floor(remaining / (1000 * 60 * 60));
            const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        };

        const timer = setInterval(() => {
            setTimeLeft(getRemainingTime());
        }, 1000);
        return () => clearInterval(timer);
    }, [activeSession]);

    // Portal rendering
    const renderVideoPortal = () => {
        if (!activeSession || !token) return null;

        const fullScreenStyles = {
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 99999,
            backgroundColor: '#111827',
            margin: 0,
            padding: 0,
            overflow: 'hidden',
        };

        const pipStyles = {
            position: 'fixed',
            bottom: '0px',
            right: '0px',
            width: isMobile ? '300px' : '350px',
            height: isMobile ? '210px' : '250px',
            zIndex: 99999,
            overflow: 'hidden',
            boxShadow: '0 -10px 40px -5px rgba(0, 0, 0, 0.4)',
            borderTopLeftRadius: '16px',
            border: '2px solid rgba(255,255,255,0.1)',
            borderBottom: 'none',
            borderRight: 'none',
            backgroundColor: '#111827'
        };

        const videoContent = (
            <div
                style={isFullScreen ? fullScreenStyles : pipStyles}
                className="transition-all duration-300"
            >
                {/* Minimize Button on top left */}
                <div className={`absolute top-4 left-4 z-[100000]`}>
                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className={`bg-gray-800/80 backdrop-blur text-white p-2 rounded-lg hover:bg-gray-700 transition-colors border border-white/10`}
                        title={isFullScreen ? "Minimize to Tray" : "Expand Full Screen"}
                    >
                        {isFullScreen ? <Minimize2 className={isFullScreen && !isMobile ? "w-5 h-5" : "w-4 h-4"} /> : <Maximize2 className={isFullScreen && !isMobile ? "w-5 h-5" : "w-4 h-4"} />}
                    </button>
                </div>

                {/* Other Controls Centered */}
                <div className="absolute top-4 left-0 right-0 pointer-events-none z-[100000] flex justify-center">
                    <div className="flex gap-2 items-center flex-wrap justify-center pointer-events-auto px-14">
                        {timeLeft && (
                            <div className={`${isFullScreen && !isMobile ? 'px-4 py-2' : 'px-2 py-1 text-xs'} rounded-lg backdrop-blur border border-white/10 font-mono font-bold flex items-center gap-2 ${timeLeft === '00:00:00' ? 'bg-red-600/90 text-white animate-pulse' : 'bg-black/50 text-white'}`}>
                                {isFullScreen && <Clock className="w-4 h-4" />}
                                {timeLeft}
                            </div>
                        )}

                        {isFullScreen && (
                            <>
                                <button
                                    onClick={() => handleDisconnect(null)}
                                    className={`bg-gray-600/80 backdrop-blur text-white ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg hover:bg-gray-700 transition-colors border border-white/10 font-medium`}
                                >
                                    Leave
                                </button>
                                {isLecturer && (
                                    <button
                                        onClick={handleEndClass}
                                        className={`bg-red-600/90 backdrop-blur text-white ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg hover:bg-red-700 transition-colors shadow-lg shadow-red-900/20 font-medium`}
                                    >
                                        {isMobile ? 'End Class' : 'End Class for All'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* When completely minimized, show a "Leave" button on hover or always */}
                {!isFullScreen && (
                    <div className="absolute top-4 right-4 z-[100000] flex gap-2 items-center opacity-0 hover:opacity-100 transition-opacity">
                        <button
                            onClick={() => handleDisconnect(null)}
                            className="bg-red-600/90 text-white px-2 py-1 rounded text-xs font-medium"
                        >
                            Leave
                        </button>
                    </div>
                )}


                <VirtualClassRoom
                    token={token}
                    roomName={activeSession.roomName}
                    onDisconnect={handleDisconnect}
                    audioEnabled={isLecturer}
                    isFullScreen={isFullScreen}
                    isLecturer={isLecturer}
                />
            </div>
        );

        return ReactDOM.createPortal(videoContent, document.body);
    };

    return (
        <LiveClassContext.Provider
            value={{
                activeSession,
                token,
                isFullScreen,
                setIsFullScreen,
                handleJoinSession,
                handleEndClass,
                handleDisconnect
            }}
        >
            {children}
            {renderVideoPortal()}
        </LiveClassContext.Provider>
    );
};
